// The container as the @inno-ui anchor: one lazy copy of every package the
// fleet consumes, registered before any remote boots. The share-reuse plugin
// picks the highest copy in use that fits, else the highest registered that
// fits, so an in-range remote gets the container's copy in any load order.
// See docs/inno-ui-shared-anchor.md.

// A namespace import keeps every export: an icon pack would ship all icons.
export const INNO_UI_SHARE_EXCLUDED = [
  '@inno-ui/icons',
  '@inno-ui/file-icons',
  '@inno-ui/app-icons'
]

export type LazyShare = {
  version: string
  get: () => Promise<() => unknown>
  shareConfig: { singleton: false; requiredVersion: string }
}

export function innoUiShares(
  loaders: Record<string, () => Promise<unknown>>,
  versions: Record<string, string>
): Record<string, LazyShare> {
  const shares: Record<string, LazyShare> = {}
  for (const [name, load] of Object.entries(loaders)) {
    const version = versions[name]
    // The drift test keeps loaders and dependencies in step; a package with no
    // installed version is skipped rather than registered under a guess.
    if (!version) continue
    shares[name] = {
      version,
      get: () => load().then(module => () => module),
      // singleton:false like every remote: a consumer whose range excludes the
      // container's copy still loads its own, which is today's behaviour.
      shareConfig: { singleton: false, requiredVersion: `^${version}` }
    }
  }
  return shares
}

export const ANCHOR_STORAGE_KEY = 'mf.innoUiAnchor'
// Backend (Pennant) flag in the 'global' scope, published before the first
// remote registers: switches the anchor off per user/org without a deploy.
// An off flag, because /features/me sends only active flags.
export const ANCHOR_OFF_FLAG = 'mf-inno-ui-anchor-off'

type AnchorSwitches = {
  /** REACT_APP_MF_INNO_UI_ANCHOR: 'off' disables; anything else leaves it on. */
  buildFlag?: string
  storage?: Pick<Storage, 'getItem'>
  /** featureFlags.active('global', ANCHOR_OFF_FLAG) */
  backendOff?: boolean
}

/**
 * localStorage['mf.innoUiAnchor'] ('on' | 'off') wins, then the backend off
 * flag, then the build flag; nothing set means on.
 */
export function isInnoUiAnchorEnabled({
  buildFlag,
  storage,
  backendOff
}: AnchorSwitches): boolean {
  let override: string | null = null
  try {
    override = storage?.getItem(ANCHOR_STORAGE_KEY) ?? null
  } catch {
    // Sandboxed frames and privacy modes throw on access; the other switches decide.
  }
  if (override === 'on') return true
  if (override === 'off') return false
  if (backendOff) return false
  return buildFlag !== 'off'
}
